import {Avatar, IconBox} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';

import {TRUST_PURPOSE_CONFIG} from '../config';

import type {FirmTrustAccountListItemDTO} from '@sollapay/types';
import type {FC, ReactNode} from 'react';

/** `sm` is the compact 34px table tile; `md` the 40px default; `lg` the grid card's tile. */
export type TrustAccountAvatarSize = 'sm' | 'md' | 'lg';

// `md` keeps IconBox's own box and icon. `lg` spans a one-line name (1.5rem line) + gap-1 + the
// number chip (h-4.5), minus the number's -mt-0.5 lift (as measured in the card story): 3.125rem.
const SIZE_CLASSES: Record<TrustAccountAvatarSize, {tile?: string; icon?: string}> = {
  sm: {tile: 'size-8.5'},
  md: {},
  lg: {tile: 'size-12.5', icon: 'size-8'}
};

interface TrustAccountAvatarProps {
  name: FirmTrustAccountListItemDTO['name'];
  trustPurpose: FirmTrustAccountListItemDTO['trustPurpose'];
  size?: TrustAccountAvatarSize;
  /** Pinned over the tile's bottom-start corner, e.g. the caller's role. */
  badge?: ReactNode;
  className?: string;
}

export const TrustAccountAvatar: FC<TrustAccountAvatarProps> = ({
  name,
  trustPurpose,
  size = 'md',
  badge,
  className
}) => {
  const icon = trustPurpose ? TRUST_PURPOSE_CONFIG[trustPurpose].icon : null;
  const sizeClasses = SIZE_CLASSES[size];
  const tileClassName = cn(sizeClasses.tile, className);

  const tile = icon ? (
    <IconBox
      icon={icon}
      variant="secondary"
      className={cn('relative overflow-hidden', tileClassName)}
      iconClassName={sizeClasses.icon}
    />
  ) : (
    <Avatar
      initials={name.slice(0, 2).toUpperCase()}
      shape="square"
      size="lg"
      noShadow
      className={tileClassName}
    />
  );

  if (!badge) return tile;

  return (
    <span data-slot="trust-account-avatar" className="relative inline-flex shrink-0">
      {tile}
      <span className="absolute -bottom-1.25 -start-1.25 z-10">{badge}</span>
    </span>
  );
};
